"use client"

import { useState, useEffect, useRef } from "react"
import styled from "styled-components"
import { 
  MessageSquare, 
  Send, 
  ArrowLeft, 
  User,
  Users,
  Lock,
  Eye,
  EyeOff,
  LogIn,
  RefreshCw
} from "lucide-react"
import SwipeMatching from "../components/SwipeMatching"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"
import { useNavigate } from "react-router-dom"
import antsTalkService from "../services/antstalkService"

// 메인 컨테이너
const Container = styled.div`
  display: flex;
  height: calc(100vh - 80px);
  max-width: 1200px;
  margin: 0 auto;
  background-color: ${props => props.theme.backgroundColor};

  @media (max-width: 1024px) {
    height: calc(100vh - 60px);
  }
`

// 좌측 채팅방 리스트
const RoomList = styled.div`
  width: 300px;
  background-color: ${props => props.theme.cardBackground};
  border-right: 1px solid ${props => props.theme.border};
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    width: 100%;
    display: ${props => props.showMobile ? 'flex' : 'none'};
  }
`

const RoomListHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${props => props.theme.border};
  background-color: ${props => props.theme.cardBackground};
`

const RoomListTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  display: flex;
  align-items: center;
  gap: 8px;
`

const RoomsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
`

const RoomItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${props => props.theme.borderLight};
  cursor: pointer;
  background-color: ${props => props.active ? props.theme.hoverBackground : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
`

const RoomInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
`

const RoomName = styled.div`
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  font-size: 14px;
`

const RoomTime = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textMuted};
`

const LastMessage = styled.div`
  font-size: 13px;
  color: ${props => props.theme.textSecondary};
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const RoomAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.$avatar
    ? `url(${props.$avatar}) center/cover no-repeat`
    : 'linear-gradient(135deg, #FF6B35, #FF8C42)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
  margin-right: 12px;
  flex-shrink: 0;
  overflow: hidden;
`

const RoomContent = styled.div`
  display: flex;
  align-items: center;
`

const RoomDetails = styled.div`
  flex: 1;
  min-width: 0;
`

// 우측 채팅 영역
const ChatArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.backgroundColor};

  @media (max-width: 768px) {
    display: ${props => props.showMobile ? 'flex' : 'none'};
  }
`

const ChatHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${props => props.theme.border};
  background-color: ${props => props.theme.cardBackground};
  display: flex;
  align-items: center;
  gap: 12px;
`

const BackButton = styled.button`
  display: none;
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: ${props => props.theme.textSecondary};
  border-radius: 6px;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const ChatTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
`

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const MessageGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
  gap: 4px;
`

const Message = styled.div`
  max-width: 70%;
  padding: 12px 16px;
  border-radius: ${props => props.isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px'};
  background-color: ${props => props.isOwn ? '#2563eb' : props.theme.cardBackground};
  color: ${props => props.isOwn ? 'white' : props.theme.textPrimary};
  word-wrap: break-word;
  line-height: 1.4;
  border: ${props => props.isOwn ? 'none' : `1px solid ${props.theme.borderLight}`};
`

const MessageTime = styled.div`
  font-size: 11px;
  color: ${props => props.theme.textMuted};
  margin-top: 2px;
`

// 메시지 입력 영역
const InputArea = styled.div`
  padding: 16px;
  border-top: 1px solid ${props => props.theme.border};
  background-color: ${props => props.theme.cardBackground};
`

const InputContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`

const MessageInput = styled.textarea`
  flex: 1;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  background-color: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};
  resize: none;
  min-height: 44px;
  max-height: 120px;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${props => props.theme.inputFocus};
  }

  &::placeholder {
    color: ${props => props.theme.textMuted};
  }
`

const SendButton = styled.button`
  background: linear-gradient(135deg, #FF6B35, #FF8C42);
  border: none;
  border-radius: 12px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover:not(:disabled) {
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

// 로그인 폼
const LoginCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  padding: 24px;
  box-shadow: ${props => props.theme.shadow};
  max-width: 400px;
  margin: 0 auto;
`

const LoginTitle = styled.h3`
  text-align: center;
  margin: 0 0 20px 0;
  color: ${props => props.theme.textPrimary};
  font-size: 18px;
  font-weight: 600;
`

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const InputGroup = styled.div`
  position: relative;
`

const InputLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  color: ${props => props.theme.textPrimary};
  font-size: 14px;
  font-weight: 500;
`

const LoginInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 8px;
  background-color: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.inputFocus};
  }
`

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${props => props.theme.textMuted};
  cursor: pointer;
  padding: 4px;
`

const LoginButton = styled.button`
  background: linear-gradient(135deg, #FF6B35, #FF8C42);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ErrorMessage = styled.div`
  color: ${props => props.theme.error};
  font-size: 12px;
  text-align: center;
  margin-top: 8px;
`

const MatchButton = styled.button`
  background: linear-gradient(135deg, #ff6b6b, #ee5a5a);
  border: none;
  border-radius: 8px;
  padding: 6px 8px;
  cursor: pointer;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 8px rgba(255,107,107,0.4);
  }
`

const FloatingMatchButton = styled.button`
  position: fixed;
  bottom: 80px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff6b6b, #ee5a5a);
  border: none;
  color: white;
  box-shadow: 0 4px 12px rgba(255,107,107,0.4);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 900;
  transition: all 0.2s;


  &:hover {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textMuted};
  text-align: center;
  padding: 40px;

  .icon {
    opacity: 0.3;
    margin-bottom: 16px;
  }

  h3 {
    margin: 0 0 8px 0;
    color: ${props => props.theme.textPrimary};
    font-size: 16px;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: ${props => props.theme.textMuted};

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`

export default function DirectMessagesPage() {
  const { theme } = useTheme()
  const { isLoggedIn, user } = useAuth()
  const navigate = useNavigate()
  
  // 상태 관리
  const [isAntsTalkAuthenticated, setIsAntsTalkAuthenticated] = useState(false)
  const [showLogin, setShowLogin] = useState(false)
  const [loginForm, setLoginForm] = useState({ user_account: '', user_pwd: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [loginError, setLoginError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  
  const [rooms, setRooms] = useState([])
  const [loadingRooms, setLoadingRooms] = useState(true)
  const [selectedRoom, setSelectedRoom] = useState(null)
  const [messages, setMessages] = useState([])
  const [messageInput, setMessageInput] = useState('')
  const [sendingMessage, setSendingMessage] = useState(false)
  
  // 모바일 뷰 상태
  const [showRoomList, setShowRoomList] = useState(true)
  const [showMatching, setShowMatching] = useState(false)
  
  // 폴링 및 참조
  const messagesEndRef = useRef(null)
  const pollingIntervalRef = useRef(null)
  const lastMessageIdRef = useRef(null)

  // 로그인 확인 (Story 세션 사용 — 앤츠톡 별도 로그인 불필요)
  useEffect(() => {
    if (!isLoggedIn) return
    setIsAntsTalkAuthenticated(true)
    setShowLogin(false)
    loadRooms()
  }, [isLoggedIn])

  const getAuthHeaders = () => {
    const username = user?.username || user?.id
    return username ? { 'X-Username': String(username) } : {}
  }

  // 채팅방 목록 로드 (Story DM conversations)
  const loadRooms = async () => {
    setLoadingRooms(true)
    try {
      const res = await fetch('/messages/conversations', { credentials: 'include', headers: getAuthHeaders() })
      const data = await res.json()
      if (data.success && data.conversations) {
        setRooms(data.conversations.map(c => ({
          id: c.id,
          name: c.participant?.full_name || c.participant?.username || '알 수 없음',
          avatar_url: c.participant?.avatar_url,
          last_message: c.lastMessage?.content || '',
          last_message_time: c.lastMessage?.timestamp || c.created_at,
          participant: c.participant
        })))
      } else {
        setRooms([])
      }
    } catch (error) {
      console.error('Failed to load rooms:', error)
      setRooms([])
    } finally {
      setLoadingRooms(false)
    }
  }

  // 메시지 로드 (Story DM)
  const loadMessages = async (roomId) => {
    try {
      const res = await fetch(`/messages/conversations/${roomId}/messages`, { credentials: 'include', headers: getAuthHeaders() })
      const data = await res.json()
      if (data.success && data.messages) {
        setMessages(data.messages)
        if (data.messages.length > 0) {
          lastMessageIdRef.current = data.messages[data.messages.length - 1].id
        }
      }
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    } catch (error) {
      console.error('Failed to load messages:', error)
    }
  }

  // 폴링으로 새 메시지 체크
  const startPolling = (roomId) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        await loadMessages(roomId)
      } catch (error) {
        console.error('Polling error:', error)
      }
    }, 10000) // 10초마다 체크
  }

  // 컴포넌트 언마운트 시 폴링 정리
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [])

  // 앤츠톡 로그인 처리
  const handleAntsTalkLogin = async (e) => {
    e.preventDefault()
    setLoginLoading(true)
    setLoginError('')

    try {
      const result = await antsTalkService.login(
        loginForm.user_account,
        loginForm.user_pwd
      )
      
      if (result.success) {
        setIsAntsTalkAuthenticated(true)
        setShowLogin(false)
        await loadRooms()
      } else {
        setLoginError(result.message || '로그인에 실패했습니다.')
      }
    } catch (error) {
      setLoginError('로그인 중 오류가 발생했습니다.')
    } finally {
      setLoginLoading(false)
    }
  }

  // 채팅방 선택
  const handleRoomSelect = async (room) => {
    setSelectedRoom(room)
    setShowMatching(false)  // 매칭 모드 해제
    setShowRoomList(false) // 모바일에서 채팅 화면으로 전환
    await loadMessages(room.id)
    startPolling(room.id)
  }

  // 메시지 전송 (Story DM)
  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!messageInput.trim() || !selectedRoom || sendingMessage) return

    setSendingMessage(true)
    
    try {
      const res = await fetch(`/messages/conversations/${selectedRoom.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ content: messageInput.trim() })
      })
      const data = await res.json()
      
      if (data.success) {
        setMessageInput('')
        await loadMessages(selectedRoom.id)
      }
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      setSendingMessage(false)
    }
  }

  // 엔터키 처리
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage(e)
    }
  }

  // 뒤로가기 (모바일)
  const handleBack = () => {
    setShowRoomList(true)
    setSelectedRoom(null)
    
    // 폴링 중지
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
    }
  }

  // 로그인하지 않은 경우
  if (!isLoggedIn) {
    return (
      <Container theme={theme}>
        <EmptyState theme={theme}>
          <MessageSquare size={64} className="icon" />
          <h3>메시지를 사용하려면 로그인하세요</h3>
          <p>Story.AntsNest에 로그인한 후 앤츠톡을 이용할 수 있습니다.</p>
        </EmptyState>
      </Container>
    )
  }

  // 앤츠톡 로그인 필요
  if (showLogin) {
    return (
      <Container theme={theme}>
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '20px'
        }}>
          <LoginCard theme={theme}>
            <LoginTitle theme={theme}>
              <MessageSquare size={20} style={{ marginRight: '8px', verticalAlign: 'text-bottom' }} />
              앤츠톡 로그인
            </LoginTitle>
            <LoginForm onSubmit={handleAntsTalkLogin}>
              <InputGroup>
                <InputLabel theme={theme}>이메일</InputLabel>
                <LoginInput
                  theme={theme}
                  type="email"
                  value={loginForm.user_account}
                  onChange={(e) => setLoginForm(prev => ({ ...prev, user_account: e.target.value }))}
                  placeholder="이메일을 입력하세요"
                  required
                />
              </InputGroup>
              
              <InputGroup>
                <InputLabel theme={theme}>비밀번호</InputLabel>
                <div style={{ position: 'relative' }}>
                  <LoginInput
                    theme={theme}
                    type={showPassword ? 'text' : 'password'}
                    value={loginForm.user_pwd}
                    onChange={(e) => setLoginForm(prev => ({ ...prev, user_pwd: e.target.value }))}
                    placeholder="비밀번호를 입력하세요"
                    style={{ paddingRight: '44px' }}
                    required
                  />
                  <PasswordToggle
                    theme={theme}
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </PasswordToggle>
                </div>
              </InputGroup>
              
              <LoginButton type="submit" disabled={loginLoading}>
                {loginLoading ? <RefreshCw size={16} /> : <LogIn size={16} />}
                {loginLoading ? '로그인 중...' : '로그인'}
              </LoginButton>
              
              {loginError && <ErrorMessage theme={theme}>{loginError}</ErrorMessage>}
            </LoginForm>
          </LoginCard>
        </div>
      </Container>
    )
  }

  return (
    <Container theme={theme}>
      {/* 채팅방 목록 */}
      <RoomList theme={theme} showMobile={showRoomList}>
        <RoomListHeader theme={theme}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <RoomListTitle theme={theme}>
              <MessageSquare size={18} />
              채팅방
            </RoomListTitle>
            <MatchButton onClick={() => { setShowMatching(true); setSelectedRoom(null); }} title="미팅 매칭">
              <Users size={16} />
            </MatchButton>
          </div>
        </RoomListHeader>
        
        <RoomsContainer>
          {loadingRooms ? (
            <LoadingSpinner theme={theme}>
              <RefreshCw size={16} />
              <span style={{ marginLeft: '8px' }}>채팅방 로딩 중...</span>
            </LoadingSpinner>
          ) : rooms.length === 0 ? (
            <LoadingSpinner theme={theme}>
              <span>아직 대화가 없습니다</span>
            </LoadingSpinner>
          ) : (
            rooms.map((room) => (
              <RoomItem
                key={room.id}
                theme={theme}
                active={selectedRoom?.id === room.id}
                onClick={() => handleRoomSelect(room)}
              >
                <RoomContent>
                  <RoomAvatar $avatar={room.avatar_url}>
                    {!room.avatar_url && (room.name ? room.name.charAt(0).toUpperCase() : <User size={20} />)}
                  </RoomAvatar>
                  <RoomDetails>
                    <RoomInfo>
                      <RoomName theme={theme}>
                        {room.name || `채팅방 ${room.id.slice(0, 8)}`}
                      </RoomName>
                      <RoomTime theme={theme}>
                        {room.last_message_time ? 
                          new Date(room.last_message_time).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' }) : 
                          ''
                        }
                      </RoomTime>
                    </RoomInfo>
                    <LastMessage theme={theme}>
                      {room.last_message || '메시지가 없습니다.'}
                    </LastMessage>
                  </RoomDetails>
                </RoomContent>
              </RoomItem>
            ))
          )}
        </RoomsContainer>
      </RoomList>

      {/* 채팅 영역 / 매칭 화면 */}
      <ChatArea theme={theme} showMobile={!showRoomList}>
        {showMatching ? (
          <SwipeMatching 
            onClose={() => setShowMatching(false)} 
            onStartChat={(conversation) => {
              // 대화방 열기 → 매칭 닫고 채팅 전환
              setShowMatching(false)
              handleRoomSelect({
                id: conversation.id,
                name: conversation.participant?.full_name || conversation.participant?.username,
                ...conversation
              })
              loadRooms() // 목록 새로고침
            }}
          />
        ) : selectedRoom ? (
          <>
            <ChatHeader theme={theme}>
              <BackButton theme={theme} onClick={handleBack}>
                <ArrowLeft size={20} />
              </BackButton>
              <RoomAvatar $avatar={selectedRoom.avatar_url} style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                {!selectedRoom.avatar_url && (selectedRoom.name ? selectedRoom.name.charAt(0).toUpperCase() : <User size={16} />)}
              </RoomAvatar>
              <ChatTitle theme={theme}>
                {selectedRoom.name || `채팅방 ${selectedRoom.id.slice(0, 8)}`}
              </ChatTitle>
            </ChatHeader>

            <MessagesContainer>
              {messages.map((message, index) => {
                const isOwn = message.sender_id === user?.id
                return (
                  <MessageGroup key={message.id || index} isOwn={isOwn}>
                    <Message theme={theme} isOwn={isOwn}>
                      {message.content || message.text}
                    </Message>
                    <MessageTime theme={theme}>
                      {message.created_at ? new Date(message.created_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false }) : ''}
                    </MessageTime>
                  </MessageGroup>
                )
              })}
              <div ref={messagesEndRef} />
            </MessagesContainer>

            <InputArea theme={theme}>
              <form onSubmit={handleSendMessage}>
                <InputContainer>
                  <MessageInput
                    theme={theme}
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="메시지를 입력하세요..."
                    rows={1}
                  />
                  <SendButton type="submit" disabled={!messageInput.trim() || sendingMessage}>
                    <Send size={18} />
                  </SendButton>
                </InputContainer>
              </form>
            </InputArea>
          </>
        ) : (
          <EmptyState theme={theme}>
            <MessageSquare size={48} className="icon" />
            <h3>채팅방을 선택하세요</h3>
            <p>좌측에서 채팅방을 선택하면 대화를 시작할 수 있습니다.</p>
          </EmptyState>
        )}
      </ChatArea>

      {/* 모바일 플로팅 매칭 버튼 */}
      <FloatingMatchButton 
        showRoomList={showRoomList}
        onClick={() => { setShowMatching(true); setSelectedRoom(null); setShowRoomList(false); }}
        title="미팅 매칭"
      >
        <Users size={24} />
      </FloatingMatchButton>
    </Container>
  )
}